import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import BaseWrapper from '../base-wrapper';
import s from './styles.module.scss';

import type { IBaseComponentProps } from 'infrastructure/interfaces';

export interface IBaseTab<TKey = string | number> extends IBaseComponentProps {
  key: TKey;
  label: string;
  disabled?: boolean;
  flat?: boolean;
  render?: boolean;
}

export interface IBaseTabsProps<TKey = string | number>
  extends IBaseComponentProps {
  tabs: Array<IBaseTab<TKey>>;
  defaultIndex?: number;
  activeKey?: TKey;
  size?: 'small' | 'default';
  type?: 'rounded' | 'default';
  disabled?: boolean;
  contentClassName?: string;
  maxWidth?: string;
  onChange?: (tab: IBaseTab<TKey>, index: number) => void;
  wrapperClassName?: string;
}

const safetyIndex = (index: number) => (index < 0 ? 0 : index);

const getActiveTab = (
  tabs: IBaseTabsProps['tabs'],
  activeKey: IBaseTabsProps['activeKey'],
  index: number,
): IBaseTab<any> => {
  if (activeKey) {
    const tab = tabs.find((t) => t.key === activeKey);
    if (tab) return tab;
  }
  return tabs[safetyIndex(index)];
};

const BaseTabs = <TKey extends string | number>(
  props: IBaseTabsProps<TKey>,
) => {
  const {
    tabs,
    onChange,
    defaultIndex = 0,
    activeKey,
    disabled = false,
    size = 'default',
    type = 'default',
    style,
    className,
    contentClassName,
    wrapperClassName,
    maxWidth,
  } = props;

  const [selectedTab, setSelectedTab] = useState<IBaseTab<TKey>>(
    getActiveTab(tabs, activeKey, defaultIndex),
  );

  const tabsClassNames = classNames(
    s['tabs-wrapper'],
    s[size],
    className,
    {
      [s.disabled]: disabled,
    },
    s[type],
  );

  const tabClassNames = (tab: IBaseTab) =>
    classNames(
      tab.className,
      s['tab-item'],
      {
        [s.active]: tab.key === selectedTab.key,
        [s.disabled]: tab.disabled,
        [s.single]: tabs.length === 1,
      },
      s[type],
    );

  const contentClassNames = classNames(s.content, contentClassName, {
    [s.disabled]: disabled,
    [s.flat]: selectedTab.flat,
  });

  const onTabClick = (tab: IBaseTab<TKey>, index: number) => {
    setSelectedTab(tab);
    if (onChange) onChange(tab, index);
  };

  useEffect(() => {
    if (tabs.length === 1) {
      setSelectedTab(tabs[0]);
      return;
    }

    setSelectedTab(tabs[safetyIndex(defaultIndex)]);
  }, [defaultIndex]);

  useEffect(() => {
    if (activeKey) {
      const tab = tabs.find((t) => t.key === activeKey);
      if (tab) setSelectedTab(tab);
    }
  }, [activeKey]);

  return (
    <div className={wrapperClassName}>
      <div style={{ width: maxWidth }} className={s['tabs-scroll-container']}>
        <ul style={style} className={tabsClassNames}>
          {tabs.map((tab, index) => (
            <li
              data-cy={`tab-${tab.label}`}
              style={tab.style}
              key={`${tab.key}-${index}`}
              className={tabClassNames(tab)}
              onClick={() => onTabClick(tab, index)}
            >
              {tab.label}
            </li>
          ))}
        </ul>
      </div>
      {selectedTab.children && (
        <BaseWrapper className={contentClassNames}>
          {selectedTab.children}
        </BaseWrapper>
      )}
    </div>
  );
};

export default BaseTabs;
